import React, { useRef, useState } from "react";
import { uploadFile } from "../lib/upload";

const baseStyle = {
  border: "1px dashed var(--fw-line-strong)",
  borderRadius: "var(--fw-r-md)",
  padding: 16,
  background: "var(--fw-cream-2)",
  cursor: "pointer",
  textAlign: "center",
};

const accept = "image/*";

const Thumb = ({ url, onRemove }) => (
  <div
    style={{
      position: "relative",
      width: 88,
      height: 88,
      borderRadius: "var(--fw-r-sm)",
      overflow: "hidden",
      border: "1px solid var(--fw-line-strong)",
      background: "#000",
      flexShrink: 0,
    }}
  >
    <img src={url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
    {onRemove && (
      <button
        type="button"
        onClick={onRemove}
        style={{
          position: "absolute",
          top: 4,
          right: 4,
          width: 22,
          height: 22,
          borderRadius: 11,
          border: "none",
          background: "rgba(17,17,16,0.85)",
          color: "var(--fw-cream)",
          cursor: "pointer",
          fontSize: 14,
          lineHeight: 1,
        }}
        aria-label="Remove image"
      >
        ×
      </button>
    )}
  </div>
);

const Dropzone = ({ multiple, onPick, busy, label }) => {
  const inputRef = useRef(null);
  const [over, setOver] = useState(false);

  const onChange = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length) onPick(files);
    e.target.value = "";
  };

  const onDrop = (e) => {
    e.preventDefault();
    setOver(false);
    const files = Array.from(e.dataTransfer.files || []).filter((f) => f.type.startsWith("image/"));
    if (files.length) onPick(multiple ? files : files.slice(0, 1));
  };

  return (
    <div
      onClick={() => !busy && inputRef.current?.click()}
      onDragOver={(e) => { e.preventDefault(); setOver(true); }}
      onDragLeave={() => setOver(false)}
      onDrop={onDrop}
      style={{
        ...baseStyle,
        opacity: busy ? 0.6 : 1,
        borderColor: over ? "var(--fw-red)" : "var(--fw-line-strong)",
        background: over ? "var(--fw-cream)" : baseStyle.background,
      }}
    >
      <div style={{ fontFamily: "var(--fw-mono)", fontSize: 11, letterSpacing: 1.4, textTransform: "uppercase", color: "var(--fw-muted)" }}>
        {busy ? "Uploading…" : label}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        multiple={!!multiple}
        onChange={onChange}
        style={{ display: "none" }}
      />
    </div>
  );
};

export const ImageUploader = ({ value, onChange, folder = "uploads/case-studies" }) => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const handlePick = async (files) => {
    setError(null);
    setBusy(true);
    try {
      const { url } = await uploadFile(files[0], { folder });
      onChange(url);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {value ? (
        <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
          <Thumb url={value} onRemove={() => onChange("")} />
          <button type="button" className="fw-btn fw-btn--ghost" onClick={() => onChange("")}>Replace</button>
        </div>
      ) : (
        <Dropzone onPick={handlePick} busy={busy} label="Drop image or click to upload" />
      )}
      {error && <div style={{ color: "var(--fw-red)", fontSize: 12 }}>{error}</div>}
    </div>
  );
};

export const ImageGalleryUploader = ({ value = [], onChange, folder = "uploads/case-studies" }) => {
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [error, setError] = useState(null);

  const handlePick = async (files) => {
    setError(null);
    setBusy(true);
    setProgress({ done: 0, total: files.length });
    try {
      const urls = [];
      for (const f of files) {
        const { url } = await uploadFile(f, { folder });
        urls.push(url);
        setProgress((p) => ({ ...p, done: p.done + 1 }));
      }
      onChange([...value, ...urls]);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const remove = (i) => onChange(value.filter((_, idx) => idx !== i));

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {value.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
          {value.map((url, i) => (
            <Thumb key={url + i} url={url} onRemove={() => remove(i)} />
          ))}
        </div>
      )}
      <Dropzone
        multiple
        onPick={handlePick}
        busy={busy}
        label={busy ? `Uploading ${progress.done}/${progress.total}…` : "Drop images or click to add (multiple)"}
      />
      {error && <div style={{ color: "var(--fw-red)", fontSize: 12 }}>{error}</div>}
    </div>
  );
};
